import React, { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ChangeRequestBox from './ChangeRequestBox/ChangeRequestBox';
import { useParams } from 'react-router';
import AccordionPlanReceived from './AccordionPlanReceived';
import AccordionChangeRequest from './AccordionChangeRequest';
import axios from '../../../axios-default';
import LoadingSpinner from '../../../components/Loader/LoadingSpinner';
import { Box, Tooltip } from '@material-ui/core';
import { easySnackbar } from '../../../store/Noti/noti.actions';
import { RequestCommentContext } from '../hooks/useRequestCommentContext';
import useCmBoxExpanded from '../hooks/useCmBoxExpanded';
import { useHistory } from 'react-router-dom';
import { useLang } from '../../../util/lang';
import { canApproveTreatmentPlan } from './approvalUtils';

// Column panel next to simulation
const RequestCommentSection = ({
    handleCurrentTreatmentPlan,
    currentTreatmentPlan,
    handleApproveTreatmentPlanByCaseType,
    treatmentPlans = [],
    patientInfo,
}) => {
    const dispatch = useDispatch();
    const { id: caseId } = useParams();
    const history = useHistory();
    const get = useLang();

    const [loading, setLoading] = useState(false);
    const [requestCommentBoxLog, setRequestCommentBoxLog] = useState([]);
    const triggerRefetch = useSelector((state) => state.requestComment.triggerRefetch);
    const { cmBoxExpanded, handleCmBoxExpanded, handleCmBoxCommentEditing } = useCmBoxExpanded({
        requestCommentBoxLog,
    });

    /**
     * Checks whether a plan can be approved using the current case data.
     */
    const getIsEnablePlanApproval = (planId = 0) =>
        canApproveTreatmentPlan({
            planId,
            treatmentPlans,
            caseStatus: patientInfo?.case_status,
        });

    const mapRequestCommentBox = (rcb) => {
        if (rcb.length === 0) {
            return <></>;
        } else {
            let refinement_no = -1;
            return rcb
                .map((plan, i) => {
                    const matchExpanded = cmBoxExpanded.find((item) => item.planId === plan.plan_id);
                    let expanded = matchExpanded ? matchExpanded.expanded : false;
                    let editing = matchExpanded ? matchExpanded.isEditing : false;
                    let isEnablePlanApproval = getIsEnablePlanApproval(plan.plan_id);
                    if (plan.refinement_no !== refinement_no) {
                        refinement_no = plan.refinement_no;
                        return (
                            <AccordionPlanReceived
                                key={`R${plan.refinement_no}P${plan.plan_no}I${i}`}
                                plan={plan}
                                index={i + 1}
                                caseId={caseId}
                                latest={i === rcb.length - 1}
                                handleCmBoxExpanded={(e) => {
                                    handleCmBoxExpanded(e);
                                }}
                                handleCmBoxClick={(e) => {
                                    // Set the selected treatment plan.
                                    // If a plan exists and the accordion is currently closed,
                                    // select the plan when opening the accordion.
                                    if (e && e !== '') {
                                        handleCurrentTreatmentPlan(e);
                                        // history.replace(`/clinicalSimulation/${caseId}/plan/${e}?treatmentPlan=${i + 1}`);
                                    } else {
                                        alert('Unable to find the selected treatment plan.');
                                    }
                                }}
                                cmBoxExpanded={expanded}
                                isEditingComment={editing}
                                currentTreatmentPlan={currentTreatmentPlan}
                                handleApproveTreatmentPlanByCaseType={handleApproveTreatmentPlanByCaseType}
                                isEnablePlanApproval={isEnablePlanApproval}
                            />
                        );
                    } else {
                        return (
                            <AccordionChangeRequest
                                key={`R${plan.refinement_no}P${plan.plan_no}I${i}`}
                                plan={plan}
                                index={i + 1}
                                latest={i === rcb.length - 1}
                                handleCmBoxExpanded={(e) => {
                                    handleCmBoxExpanded(e);
                                }}
                                handleCmBoxClick={(e) => {
                                    // Set the selected treatment plan.
                                    // If a plan exists and the accordion is currently closed,
                                    // select the plan when opening the accordion.
                                    if (e && e !== '') {
                                        handleCurrentTreatmentPlan(e);
                                        // history.replace(`/clinicalSimulation/${caseId}/plan/${e}?treatmentPlan=${i + 1}`);
                                    } else {
                                        alert('Unable to find the selected treatment plan.');
                                    }
                                }}
                                cmBoxExpanded={expanded}
                                isEditingComment={editing}
                                currentTreatmentPlan={currentTreatmentPlan}
                                handleApproveTreatmentPlanByCaseType={handleApproveTreatmentPlanByCaseType}
                                isEnablePlanApproval={isEnablePlanApproval}
                            />
                        );
                    }
                })
                .reverse();
        }
    };

    const getData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/case/requestCommentLog/${caseId}`);
            const commentsData = response.data.result;
            const isSame = JSON.stringify(requestCommentBoxLog) === JSON.stringify(commentsData);
            if (isSame) return;
            setRequestCommentBoxLog(commentsData);
        } catch (error) {
            console.error(error);
            dispatch(easySnackbar(error?.message, 'error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log(patientInfo, 'patientInfo');
        getData();
    }, [triggerRefetch]);

    return (
        <Box position={'relative'} minHeight={200} style={{ padding: "8px" }}>
            <RequestCommentContext.Provider value={{ setLoading, loading, handleCmBoxCommentEditing, }} >
                {loading && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        zIndex={5}
                        style={{
                            background: '#0000000f',
                        }}
                    >
                        <LoadingSpinner />
                    </Box>
                )}

                <ChangeRequestBox />
                {mapRequestCommentBox(requestCommentBoxLog)}

                {/* Show the status of plan */}
                {/* @dev - You can change this condition according to data and requirement - (Done) */}
                {patientInfo?.case_status && (
                    <Box
                        style={{
                            borderRadius: 4,
                            backgroundColor:
                                patientInfo.case_status === 'PLAN_APPROVED' ? 'rgb(80, 77, 148)' : 'rgb(233, 233, 233)',
                            fontWeight: '600',
                            padding: '18px 16px',
                            fontSize: '14px',
                            color: patientInfo.case_status === 'PLAN_APPROVED' ? '#fff' : 'gray',
                        }}
                    >
                        {get(`@global.caseStatus.${patientInfo?.case_status}`)}
                    </Box>
                )}
            </RequestCommentContext.Provider>
        </Box>
    );
};
export default memo(RequestCommentSection);
